import React from 'react';

/**
 * "Is this component still mounted?", for async work that must not `setState` after teardown.
 *
 * The obvious implementation — a ref set to `true` in an effect's cleanup — is WRONG, and was
 * the cause of the SPEI checkout hanging on "Getting your details…" with no `/session/invoice`
 * request. React runs an effect's cleanup WITHOUT the component going away in two ordinary
 * situations:
 *
 * - **Vite Fast Refresh.** An HMR update re-runs effects while preserving state and refs, so the
 *   cleanup's flag survives into a component that is still on screen.
 * - **StrictMode.** React 18 deliberately double-invokes effects (setup → cleanup → setup).
 *
 * After either, a set-only-on-cleanup flag claims the component is gone while it is alive. Every
 * `if (gone) return` then bails mid-flight and every `if (!gone) setLoading(false)` is skipped,
 * which is exactly how a checkout ends up permanently stuck in its busy state.
 *
 * The fix is to treat liveness as something the effect ASSERTS on every setup, not merely
 * revokes on teardown.
 */

/** The pure state behind {@link useIsAlive} — no React, so the invariant is unit-testable. */
export interface Liveness {
  /** Effect setup: the component is (still) mounted. */
  activate: () => void;
  /** Effect cleanup: the component is going away. */
  deactivate: () => void;
  /** Whether an async continuation may still touch component state. */
  isAlive: () => boolean;
}

/**
 * Create a liveness flag. Starts DEAD: nothing may assume it can setState until the effect has
 * actually run. `activate` after a `deactivate` restores it — that re-mount case is the whole
 * point (see the module doc).
 */
export function createLiveness(): Liveness {
  let alive = false;
  return {
    activate: () => {
      alive = true;
    },
    deactivate: () => {
      alive = false;
    },
    isAlive: () => alive,
  };
}

/**
 * React binding for {@link createLiveness}. Returns a stable `isAlive()` to guard async
 * continuations with:
 *
 * ```ts
 * const isAlive = useIsAlive();
 * // …await something…
 * if (!isAlive()) return;
 * ```
 */
export function useIsAlive(): () => boolean {
  const liveness = React.useRef<Liveness | null>(null);
  if (liveness.current === null) liveness.current = createLiveness();
  const current = liveness.current;

  React.useEffect(() => {
    // Asserted on EVERY setup, not just revoked on teardown — that is the fix.
    current.activate();
    return () => current.deactivate();
  }, [current]);

  return React.useCallback(() => current.isAlive(), [current]);
}
